import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  AlertTriangle,
  Check,
  CheckCircle2,
  ClipboardList,
  Download,
  History as HistoryIcon,
  Inbox,
  LifeBuoy,
  MessageCircleQuestion,
  RefreshCw,
  Search,
  Send,
  TrendingUp,
  Users,
  X,
} from 'lucide-react';
import PageHeader from '../../components/PageHeader';
import LoadingSpinner from '../../components/LoadingSpinner';
import { PortalRosterRow, portalMentorService } from '../../services/api';
import {
  Avatar,
  EmptyState,
  HEALTH,
  HealthBadge,
  Progress,
  StatCard,
  daysSince,
  fa,
  fullName,
  inputClass,
  timeAgo,
} from './mentor/MentorUI';
import { MentorTrendTab } from './mentor/MentorTrendTab';
import { MentorTasksTab } from './mentor/MentorTasksTab';
import { MentorAnnounceTab } from './mentor/MentorAnnounceTab';
import { MentorChatTab } from './mentor/MentorChatTab';

const TICKET_STATUS: Record<string, string> = {
  OPEN: 'باز',
  IN_PROGRESS: 'در حال پیگیری',
  CLOSED: 'بسته',
};

type TabKey = 'roster' | 'tasks' | 'review' | 'questions' | 'chat' | 'announce' | 'trend';

const PortalMentor: React.FC = () => {
  const [tab, setTab] = useState<TabKey>('roster');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');

  const [roster, setRoster] = useState<PortalRosterRow[]>([]);
  const [queue, setQueue] = useState<any[]>([]);
  const [questions, setQuestions] = useState<any[]>([]);
  const [mentors, setMentors] = useState<any[]>([]);
  const [tickets, setTickets] = useState<any[]>([]);
  /** «باز» صف کاری است؛ «تاریخچه» همه‌چیز را نشان می‌دهد، شامل بسته‌شده‌ها */
  const [supportView, setSupportView] = useState<'questions' | 'tickets'>('questions');
  const [showHistory, setShowHistory] = useState(false);

  const [search, setSearch] = useState('');
  const [healthFilter, setHealthFilter] = useState<string>('');

  const [openAssignment, setOpenAssignment] = useState<any>(null);
  const [review, setReview] = useState({ decision: 'APPROVED', feedback: '', score: '' });
  const [replies, setReplies] = useState<Record<string, string>>({});

  const flash = (m: string) => {
    setNotice(m);
    setTimeout(() => setNotice(''), 3000);
  };
  const fail = useCallback((err: any, fallback: string) => {
    setError(err?.response?.data?.message || fallback);
    setTimeout(() => setError(''), 6000);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [r, q, qs, m, t] = await Promise.all([
        portalMentorService.roster().catch(() => []),
        portalMentorService.reviewQueue().catch(() => []),
        portalMentorService.questions(showHistory ? 'ALL' : undefined).catch(() => []),
        portalMentorService.mentors().catch(() => []),
        portalMentorService.tickets(showHistory ? {} : { status: 'OPEN' }).catch(() => []),
      ]);
      setRoster(r);
      setQueue(q);
      setQuestions(qs);
      setMentors(m);
      setTickets(t);
    } catch (err: any) {
      fail(err, 'دریافت اطلاعات انجام نشد.');
    } finally {
      setLoading(false);
    }
  }, [fail, showHistory]);

  useEffect(() => {
    load();
  }, [load]);

  const run = async (fn: () => Promise<void>, fallback: string) => {
    setBusy(true);
    try {
      await fn();
    } catch (err: any) {
      fail(err, fallback);
    } finally {
      setBusy(false);
    }
  };

  const openReview = (assignmentId: string) =>
    run(async () => {
      const detail = await portalMentorService.assignment(assignmentId);
      setOpenAssignment(detail);
      setReview({ decision: 'APPROVED', feedback: '', score: '' });
    }, 'دریافت پاسخ انجام نشد.');

  const submitReview = () =>
    run(async () => {
      await portalMentorService.review(openAssignment.id, {
        decision: review.decision,
        feedback: review.feedback || undefined,
        score: review.score ? Number(review.score) : undefined,
      });
      setOpenAssignment(null);
      await load();
      flash('بازخورد ثبت شد.');
    }, 'ثبت بازخورد انجام نشد.');

  // ── آمار بالای صفحه ───────────────────────────────────────────────────────
  const stats = useMemo(() => {
    const red = roster.filter((r) => r.health === 'RED').length;
    const overdue = roster.reduce((a, r) => a + (r.overdueCount || 0), 0);
    const openQuestions = questions.filter((q) => q.status !== 'CLOSED').length;
    return { people: roster.length, red, overdue, review: queue.length, openQuestions };
  }, [roster, queue, questions]);

  const visibleRoster = useMemo(() => {
    const term = search.trim();
    return roster
      .filter((r) => (healthFilter ? r.health === healthFilter : true))
      .filter((r) =>
        term
          ? fullName(r.user).includes(term) || (r.user.phone || '').includes(term)
          : true,
      )
      // قرمزها اول، بعد کم‌پیشرفت‌ترین‌ها — همان ترتیبی که منتور باید رسیدگی کند
      .sort((a, b) => {
        const order: Record<string, number> = { RED: 0, YELLOW: 1, GREEN: 2 };
        const d = (order[a.health] ?? 3) - (order[b.health] ?? 3);
        return d !== 0 ? d : a.progressPercent - b.progressPercent;
      });
  }, [roster, search, healthFilter]);

  const openTickets = useMemo(
    () => tickets.filter((t) => t.status !== 'CLOSED').length,
    [tickets],
  );

  const TABS: Array<{ key: TabKey; label: string; badge?: number }> = [
    { key: 'roster', label: 'شرکت‌کنندگان من' },
    { key: 'tasks', label: 'تمرین‌ها' },
    { key: 'review', label: 'صف بررسی', badge: queue.length },
    { key: 'questions', label: 'سؤال و تیکت', badge: stats.openQuestions + openTickets },
    { key: 'chat', label: 'گفتگو' },
    { key: 'announce', label: 'پیام به گروه' },
    { key: 'trend', label: 'روند تیم' },
  ];

  if (loading) return <LoadingSpinner />;

  return (
    <div dir="rtl">
      <PageHeader
        title="پنل منتور"
        description="شرکت‌کنندگان شما، بررسی تمرین‌ها و پاسخ به سؤال‌ها"
        action={
          <button
            type="button"
            onClick={load}
            className="flex items-center gap-1.5 rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm font-bold text-gray-700 transition hover:bg-gray-50"
          >
            <RefreshCw className="h-4 w-4" />
            تازه‌سازی
          </button>
        }
      />

      {error ? (
        <div className="mb-4 flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm font-bold text-red-700">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          {error}
        </div>
      ) : null}
      {notice ? (
        <div className="mb-4 flex items-start gap-2 rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm font-bold text-green-700">
          <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" />
          {notice}
        </div>
      ) : null}

      {/* ── خلاصهٔ وضعیت ─────────────────────────────────────────────────── */}
      <div className="mb-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          label="شرکت‌کنندگان من"
          value={stats.people}
          icon={<Users className="h-4 w-4" />}
          tone="#2563eb"
          active={tab === 'roster' && !healthFilter}
          onClick={() => {
            setTab('roster');
            setHealthFilter('');
          }}
        />
        <StatCard
          label="نیازمند توجه"
          value={stats.red}
          icon={<AlertTriangle className="h-4 w-4" />}
          tone="#d03b3b"
          hint={stats.overdue ? fa(stats.overdue) + ' تسک عقب‌افتاده' : 'بدون عقب‌افتادگی'}
          active={tab === 'roster' && healthFilter === 'RED'}
          onClick={() => {
            setTab('roster');
            setHealthFilter(healthFilter === 'RED' ? '' : 'RED');
          }}
        />
        <StatCard
          label="در انتظار بررسی شما"
          value={stats.review}
          icon={<ClipboardList className="h-4 w-4" />}
          tone="#7c3aed"
          active={tab === 'review'}
          onClick={() => setTab('review')}
        />
        <StatCard
          label="سؤال باز"
          value={stats.openQuestions}
          icon={<MessageCircleQuestion className="h-4 w-4" />}
          tone="#0ca30c"
          active={tab === 'questions'}
          onClick={() => setTab('questions')}
        />
      </div>

      {/* ── تب‌ها ────────────────────────────────────────────────────────── */}
      <div className="mb-4 flex flex-wrap gap-1 border-b border-gray-200">
        {TABS.map((item) => (
          <button
            key={item.key}
            type="button"
            onClick={() => setTab(item.key)}
            className={
              'flex items-center gap-1.5 px-4 py-2 text-sm font-bold transition ' +
              (tab === item.key
                ? 'border-b-2 border-blue-600 text-blue-700'
                : 'text-gray-500 hover:text-gray-700')
            }
          >
            {item.key === 'trend' ? <TrendingUp className="h-3.5 w-3.5" /> : null}
            {item.label}
            {item.badge ? (
              <span
                className={
                  'rounded-full px-1.5 py-0.5 text-[10px] font-black ' +
                  (tab === item.key ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-600')
                }
              >
                {fa(item.badge)}
              </span>
            ) : null}
          </button>
        ))}
      </div>

      {/* ── شرکت‌کنندگان ─────────────────────────────────────────────────── */}
      {tab === 'roster' ? (
        <div className="space-y-3">
          <div className="flex flex-wrap items-center gap-2">
            <div className="relative min-w-[200px] flex-1">
              <Search className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="جست‌وجوی نام یا شماره…"
                className={inputClass + ' pr-9'}
              />
            </div>
            <div className="flex gap-1">
              {[
                { key: '', label: 'همه' },
                { key: 'RED', label: 'قرمز' },
                { key: 'YELLOW', label: 'زرد' },
                { key: 'GREEN', label: 'سبز' },
              ].map((f) => (
                <button
                  key={f.key}
                  type="button"
                  onClick={() => setHealthFilter(f.key)}
                  className={
                    'rounded-lg border px-3 py-2 text-xs font-bold transition ' +
                    (healthFilter === f.key
                      ? 'border-blue-400 bg-blue-50 text-blue-700'
                      : 'border-gray-300 bg-white text-gray-600 hover:bg-gray-50')
                  }
                >
                  {f.label}
                </button>
              ))}
            </div>
          </div>

          {!visibleRoster.length ? (
            <EmptyState
              icon={<Users className="h-5 w-5" />}
              title={
                roster.length
                  ? 'با این فیلتر کسی پیدا نشد'
                  : 'هنوز شرکت‌کننده‌ای به شما ارجاع نشده'
              }
              body={
                roster.length
                  ? 'فیلتر یا عبارت جست‌وجو را تغییر دهید.'
                  : 'مدیریت دوره باید شرکت‌کنندگان را به گروه شما اضافه کند.'
              }
            />
          ) : (
            <div className="overflow-x-auto rounded-xl border border-gray-200 bg-white">
              <table className="w-full min-w-[760px] text-right text-sm">
                <thead className="bg-gray-50 text-[11px] font-bold text-gray-500">
                  <tr>
                    <th className="px-4 py-3">شرکت‌کننده</th>
                    <th className="px-4 py-3">گروه</th>
                    <th className="px-4 py-3">وضعیت</th>
                    <th className="px-4 py-3">پیشرفت</th>
                    <th className="px-4 py-3">عقب‌افتاده</th>
                    <th className="px-4 py-3">منتظر بررسی</th>
                    <th className="px-4 py-3">آخرین فعالیت</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {visibleRoster.map((row) => (
                    <tr
                      key={row.id}
                      className={
                        'transition hover:bg-gray-50/70 ' +
                        (row.health === 'RED' ? 'bg-red-50/40' : '')
                      }
                    >
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2.5">
                          <Avatar user={row.user} tone={HEALTH[row.health]?.color} />
                          <div className="min-w-0">
                            <p className="truncate font-bold text-gray-900">
                              {fullName(row.user)}
                            </p>
                            <p className="text-[11px] tabular-nums text-gray-400">
                              {row.user.phone || '—'}
                            </p>
                          </div>
                        </div>
                      </td>
                      <td className="px-4 py-3 text-xs text-gray-600">{row.cohort.name}</td>
                      <td className="px-4 py-3">
                        <HealthBadge health={row.health} />
                      </td>
                      <td className="px-4 py-3">
                        <Progress value={row.progressPercent} />
                      </td>
                      <td className="px-4 py-3">
                        {row.overdueCount ? (
                          <span className="rounded-md bg-red-50 px-2 py-0.5 text-xs font-black tabular-nums text-red-600">
                            {fa(row.overdueCount)}
                          </span>
                        ) : (
                          <span className="text-xs text-gray-300">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3">
                        {row.awaitingReview ? (
                          <button
                            type="button"
                            onClick={() => setTab('review')}
                            className="rounded-md bg-violet-50 px-2 py-0.5 text-xs font-black tabular-nums text-violet-700 transition hover:bg-violet-100"
                          >
                            {fa(row.awaitingReview)}
                          </button>
                        ) : (
                          <span className="text-xs text-gray-300">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-[11px] text-gray-500">
                        {row.lastActiveAt ? timeAgo(row.lastActiveAt) : 'هنوز وارد نشده'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      ) : null}

      {/* ── صف بررسی ─────────────────────────────────────────────────────── */}
      {tab === 'review' ? (
        <div className="space-y-2">
          {!queue.length ? (
            <EmptyState
              icon={<Inbox className="h-5 w-5" />}
              title="صف بررسی خالی است"
              body="هر تمرینی که شرکت‌کنندگان شما بفرستند اینجا ظاهر می‌شود."
            />
          ) : (
            queue.map((item) => {
              const waiting = daysSince(item.submittedAt);
              return (
                <button
                  key={item.assignmentId}
                  type="button"
                  onClick={() => openReview(item.assignmentId)}
                  className="flex w-full items-center gap-3 rounded-xl border border-gray-200 bg-white p-3 text-right transition hover:border-blue-300 hover:shadow-sm"
                >
                  <Avatar user={item.participant.user} tone="#7c3aed" />
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-bold text-gray-900">
                      {item.task.title}
                    </span>
                    <span className="mt-0.5 block truncate text-[11px] text-gray-500">
                      {fullName(item.participant.user)} · {item.participant.cohort.name}
                      {item.version > 1 ? ' · نسخهٔ ' + fa(item.version) : ''}
                    </span>
                  </span>
                  {/* بیش از سه روز انتظار پررنگ می‌شود تا در صف طولانی گم نشود */}
                  <span
                    className={
                      'shrink-0 rounded-md px-2 py-1 text-[11px] font-bold ' +
                      (waiting >= 3
                        ? 'bg-amber-50 text-amber-700'
                        : 'bg-gray-50 text-gray-500')
                    }
                  >
                    {waiting >= 1 ? fa(waiting) + ' روز منتظر' : 'امروز'}
                  </span>
                </button>
              );
            })
          )}
        </div>
      ) : null}

      {/* ── سؤال‌ها ──────────────────────────────────────────────────────── */}
      {tab === 'questions' ? (
        <div className="space-y-3">
          {/* سؤال و تیکت دو صف جدا با دو مدل پاسخ‌اند؛ در یک فهرست قاطی‌شان نمی‌کنیم */}
          <div className="flex flex-wrap items-center gap-2 rounded-xl border border-gray-200 bg-white p-3">
            {(
              [
                ['questions', 'سؤال‌ها', questions.length],
                ['tickets', 'تیکت‌های پشتیبانی', tickets.length],
              ] as const
            ).map(([key, label, count]) => (
              <button
                key={key}
                type="button"
                onClick={() => setSupportView(key)}
                className={
                  'rounded-lg border px-3 py-1.5 text-xs font-bold transition ' +
                  (supportView === key
                    ? 'border-blue-500 bg-blue-50 text-blue-700'
                    : 'border-gray-300 text-gray-600 hover:border-gray-400')
                }
              >
                {label}
                <span className="mr-1 text-[10.5px] text-gray-400">{fa(count)}</span>
              </button>
            ))}

            <button
              type="button"
              onClick={() => setShowHistory((v) => !v)}
              className={
                'mr-auto flex items-center gap-1.5 rounded-lg border px-3 py-1.5 text-xs font-bold transition ' +
                (showHistory
                  ? 'border-violet-500 bg-violet-50 text-violet-700'
                  : 'border-gray-300 text-gray-600 hover:border-gray-400')
              }
            >
              <HistoryIcon className="h-3.5 w-3.5" />
              {showHistory ? 'نمایش تاریخچهٔ کامل' : 'فقط موارد باز'}
            </button>
          </div>

          {supportView === 'tickets' ? (
            !tickets.length ? (
              <EmptyState
                icon={<LifeBuoy className="h-5 w-5" />}
                title={showHistory ? 'تیکتی ثبت نشده' : 'تیکت بازی وجود ندارد'}
                body="تیکت‌های پشتیبانی شرکت‌کنندگان شما اینجا می‌آید."
              />
            ) : (
              <div className="space-y-3">
                {tickets.map((ticket) => (
                  <div key={ticket.id} className="rounded-xl border border-gray-200 bg-white p-4">
                    <div className="flex flex-wrap items-start justify-between gap-2">
                      <div className="flex min-w-0 items-start gap-2.5">
                        <Avatar user={ticket.participant.user} size="sm" tone="#7C3AED" />
                        <div className="min-w-0">
                          <p className="text-sm font-bold text-gray-900">{ticket.subject}</p>
                          <p className="mt-0.5 text-[11px] text-gray-500">
                            {fullName(ticket.participant.user)} ·{' '}
                            {ticket.participant.cohort?.name} · {timeAgo(ticket.createdAt)}
                          </p>
                        </div>
                      </div>
                      <span
                        className={
                          'shrink-0 rounded-full px-2 py-0.5 text-[10.5px] font-bold ' +
                          (ticket.status === 'CLOSED'
                            ? 'bg-gray-100 text-gray-500'
                            : ticket.status === 'IN_PROGRESS'
                              ? 'bg-blue-50 text-blue-700'
                              : 'bg-amber-50 text-amber-700')
                        }
                      >
                        {TICKET_STATUS[ticket.status] ?? ticket.status}
                      </span>
                    </div>

                    {ticket.body ? (
                      <p className="mt-2 whitespace-pre-wrap rounded-lg bg-gray-50 px-3 py-2 text-[12.5px] leading-6 text-gray-700">
                        {ticket.body}
                      </p>
                    ) : null}

                    {ticket.replies?.length ? (
                      <div className="mt-2 space-y-1.5">
                        {ticket.replies.map((reply: any) => (
                          <div
                            key={reply.id}
                            className={
                              'rounded-lg px-3 py-2 text-[12px] leading-6 ' +
                              (reply.isInternal
                                ? 'border border-dashed border-amber-300 bg-amber-50 text-amber-900'
                                : 'bg-blue-50/60 text-gray-700')
                            }
                          >
                            <p className="mb-0.5 text-[10.5px] font-bold text-gray-500">
                              {fullName(reply.author)} · {timeAgo(reply.createdAt)}
                              {reply.isInternal ? (
                                /* یادداشت داخلی هیچ‌وقت به شرکت‌کننده نشان داده نمی‌شود */
                                <span className="mr-1 text-amber-700">یادداشت داخلی تیم</span>
                              ) : null}
                            </p>
                            <p className="whitespace-pre-wrap">{reply.body}</p>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="mt-2 text-[11px] font-bold text-gray-400">
                        هنوز پاسخی ثبت نشده است. پاسخ تیکت از «پشتیبانی» پنل مدیریت ثبت می‌شود.
                      </p>
                    )}
                  </div>
                ))}
              </div>
            )
          ) : !questions.length ? (
            <EmptyState
              icon={<MessageCircleQuestion className="h-5 w-5" />}
              title="سؤال بازی وجود ندارد"
              body="سؤال‌های شرکت‌کنندگان شما اینجا می‌آید."
            />
          ) : (
            questions.map((question) => {
              const waiting = daysSince(question.createdAt);
              const unanswered = !question.replies?.length;
              return (
                <div
                  key={question.id}
                  className={
                    'rounded-xl border bg-white p-4 ' +
                    (unanswered && waiting >= 2 ? 'border-amber-300' : 'border-gray-200')
                  }
                >
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <div className="flex min-w-0 items-start gap-2.5">
                      <Avatar user={question.participant.user} size="sm" tone="#0ca30c" />
                      <div className="min-w-0">
                        <p className="text-sm font-bold text-gray-900">{question.subject}</p>
                        <p className="mt-0.5 text-[11px] text-gray-500">
                          {fullName(question.participant.user)} ·{' '}
                          {question.participant.cohort.name} · {timeAgo(question.createdAt)}
                        </p>
                      </div>
                    </div>
                    <div className="flex shrink-0 items-center gap-1.5">
                      {unanswered && waiting >= 2 ? (
                        <span className="rounded-full bg-amber-50 px-2 py-0.5 text-[10.5px] font-bold text-amber-700">
                          {fa(waiting)} روز بی‌پاسخ
                        </span>
                      ) : null}
                      {question.status === 'ESCALATED' ? (
                        <span className="rounded-full bg-purple-50 px-2 py-0.5 text-[10.5px] font-bold text-purple-700">
                          ارجاع‌شده
                        </span>
                      ) : null}
                    </div>
                  </div>

                  <p className="mt-2 whitespace-pre-line rounded-lg bg-gray-50 px-3 py-2 text-xs leading-7 text-gray-700">
                    {question.body}
                  </p>

                  {question.replies?.length ? (
                    <div className="mt-3 space-y-1.5 border-r-2 border-blue-100 pr-3">
                      {question.replies.map((reply: any) => (
                        <div key={reply.id} className="rounded-lg bg-blue-50/50 px-3 py-2">
                          <p className="whitespace-pre-line text-xs leading-7 text-gray-700">
                            {reply.body}
                          </p>
                          <p className="mt-1 text-[10px] font-bold text-gray-400">
                            {fullName(reply.author)}
                          </p>
                        </div>
                      ))}
                    </div>
                  ) : null}

                  <div className="mt-3 flex flex-wrap items-end gap-2">
                    <input
                      value={replies[question.id] ?? ''}
                      onChange={(e) => setReplies({ ...replies, [question.id]: e.target.value })}
                      placeholder="پاسخ شما"
                      className={inputClass + ' flex-1'}
                    />
                    <button
                      type="button"
                      disabled={busy || !(replies[question.id] ?? '').trim()}
                      onClick={() =>
                        run(async () => {
                          await portalMentorService.replyQuestion(
                            question.id,
                            replies[question.id].trim(),
                          );
                          setReplies({ ...replies, [question.id]: '' });
                          await load();
                          flash('پاسخ ثبت شد.');
                        }, 'ثبت پاسخ انجام نشد.')
                      }
                      className="flex shrink-0 items-center gap-1.5 rounded-lg bg-blue-600 px-3 py-2 text-sm font-bold text-white transition hover:bg-blue-700 disabled:opacity-40"
                    >
                      <Send className="h-4 w-4" />
                      پاسخ
                    </button>
                    <select
                      value=""
                      onChange={(e) =>
                        e.target.value &&
                        run(async () => {
                          await portalMentorService.escalateQuestion(question.id, e.target.value);
                          await load();
                          flash('سؤال ارجاع شد.');
                        }, 'ارجاع انجام نشد.')
                      }
                      className="w-40 shrink-0 rounded-lg border border-gray-300 px-2 py-2 text-xs"
                    >
                      <option value="">ارجاع به مدرس…</option>
                      {mentors.map((m: any) => (
                        <option key={m.id} value={m.id}>
                          {fullName(m)}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
              );
            })
          )}
        </div>
      ) : null}

      {/* ── روند تیم ─────────────────────────────────────────────────────── */}
      {tab === 'tasks' ? <MentorTasksTab onError={fail} /> : null}

      {tab === 'chat' ? <MentorChatTab onError={fail} /> : null}

      {tab === 'announce' ? <MentorAnnounceTab onError={fail} onFlash={flash} /> : null}

      {tab === 'trend' ? <MentorTrendTab onError={fail} /> : null}

      {/* ── پنجرهٔ بازبینی ───────────────────────────────────────────────── */}
      {openAssignment ? (
        <div
          className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-black/40 p-4"
          onClick={() => setOpenAssignment(null)}
        >
          <div
            className="my-6 w-full max-w-3xl rounded-2xl bg-white p-5 shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mb-4 flex items-start justify-between gap-3 border-b border-gray-100 pb-3">
              <div className="flex min-w-0 items-start gap-2.5">
                <Avatar user={openAssignment.participant.user} tone="#7c3aed" />
                <div className="min-w-0">
                  <p className="text-base font-bold text-gray-900">
                    {openAssignment.task.title}
                  </p>
                  <p className="mt-0.5 text-xs text-gray-500">
                    {fullName(openAssignment.participant.user)} ·{' '}
                    {openAssignment.participant.cohort.name}
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => setOpenAssignment(null)}
                className="shrink-0 rounded-lg p-1.5 text-gray-400 transition hover:bg-gray-100"
                aria-label="بستن"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            {(openAssignment.submissions ?? []).slice(0, 1).map((submission: any) => (
              <div key={submission.id} className="space-y-3">
                <p className="text-xs font-bold text-gray-500">
                  نسخهٔ {fa(submission.version)} ·{' '}
                  {new Date(submission.submittedAt).toLocaleDateString('fa-IR')}
                </p>

                {openAssignment.task.fields.map((field: any) => {
                  const value = submission.values.find((v: any) => v.fieldId === field.id);
                  return (
                    <div key={field.id} className="rounded-lg border border-gray-100 bg-gray-50/50 p-3">
                      <p className="text-xs font-bold text-gray-700">{field.label}</p>
                      {value?.textValue ? (
                        <p className="mt-1 whitespace-pre-line text-xs leading-7 text-gray-800">
                          {value.textValue}
                        </p>
                      ) : null}
                      {value?.numberValue !== null && value?.numberValue !== undefined ? (
                        <p className="mt-1 text-sm font-black text-gray-900">
                          {fa(Number(value.numberValue))}
                        </p>
                      ) : null}
                      {Array.isArray(value?.jsonValue) ? (
                        <ul className="mt-1 list-inside list-disc text-xs text-gray-700">
                          {value.jsonValue.map((item: any, i: number) => (
                            <li key={i}>{String(item)}</li>
                          ))}
                        </ul>
                      ) : null}
                      {value?.jsonValue === true ? (
                        <p className="mt-1 inline-flex items-center gap-1 text-xs font-bold text-green-700">
                          <Check className="h-3.5 w-3.5" />
                          تأیید کرده
                        </p>
                      ) : null}
                      {(value?.files ?? []).map((file: any) => (
                        <button
                          key={file.id}
                          type="button"
                          onClick={() =>
                            portalMentorService.downloadFile(file.id, file.originalName)
                          }
                          className="mt-1.5 flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-2.5 py-1.5 text-[11px] font-bold text-blue-700 transition hover:bg-blue-50"
                        >
                          <Download className="h-3.5 w-3.5" />
                          {file.originalName}
                        </button>
                      ))}
                    </div>
                  );
                })}
              </div>
            ))}

            <div className="mt-4 rounded-xl border border-blue-200 bg-blue-50/40 p-3">
              <p className="mb-2 text-sm font-bold text-gray-900">بازخورد شما</p>
              <div className="flex flex-wrap gap-2">
                {(
                  [
                    ['APPROVED', 'تأیید', '#0ca30c'],
                    ['NEEDS_REVISION', 'نیازمند اصلاح', '#fab219'],
                    ['REJECTED', 'رد', '#d03b3b'],
                  ] as const
                ).map(([key, label, color]) => (
                  <button
                    key={key}
                    type="button"
                    onClick={() => setReview({ ...review, decision: key })}
                    className="rounded-lg border px-3 py-1.5 text-xs font-bold transition"
                    style={
                      review.decision === key
                        ? { borderColor: color, backgroundColor: color + '18', color }
                        : { borderColor: '#E5E7EB', color: '#6B7280' }
                    }
                  >
                    {label}
                  </button>
                ))}
              </div>

              <textarea
                rows={3}
                value={review.feedback}
                onChange={(e) => setReview({ ...review, feedback: e.target.value })}
                placeholder="بازخورد متنی برای شرکت‌کننده"
                className={inputClass + ' mt-2'}
              />

              {openAssignment.task.maxScore ? (
                <label className="mt-2 flex items-center gap-2 text-xs font-bold text-gray-700">
                  امتیاز از {fa(openAssignment.task.maxScore)}:
                  <input
                    type="number"
                    value={review.score}
                    onChange={(e) => setReview({ ...review, score: e.target.value })}
                    className="w-20 rounded-lg border border-gray-300 px-2 py-1 text-xs"
                  />
                </label>
              ) : null}

              <button
                type="button"
                disabled={busy}
                onClick={submitReview}
                className="mt-3 rounded-lg bg-blue-600 px-4 py-2 text-sm font-bold text-white transition hover:bg-blue-700 disabled:opacity-50"
              >
                ثبت بازخورد
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default PortalMentor;
